import React, { useState, useRef, useEffect } from 'react';
import VendorCard from './VendorCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VendorCarousel = ({ 
  title,
  subtitle,
  vendors = [],
  onViewAll,
  onMessage,
  onViewProfile,
  onToggleFavorite,
  variant = 'default', // 'default', 'featured', 'compact'
  showViewAll = true
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const carouselRef = useRef(null);
  const [itemWidth, setItemWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const getItemsPerView = () => {
    if (variant === 'featured') return { mobile: 1.2, tablet: 2, desktop: 3 };
    if (variant === 'compact') return { mobile: 1.2, tablet: 2, desktop: 4 };
    return { mobile: 1.2, tablet: 2, desktop: 3 };
  };

  const itemsPerView = getItemsPerView();

  useEffect(() => {
    const updateItemWidth = () => {
      if (carouselRef.current) {
        const containerWidth = carouselRef.current.offsetWidth;
        const gap = 16; // 1rem gap
        let itemsVisible;
        
        if (window.innerWidth >= 1024) {
          itemsVisible = itemsPerView.desktop;
        } else if (window.innerWidth >= 768) {
          itemsVisible = itemsPerView.tablet;
        } else {
          itemsVisible = itemsPerView.mobile;
        }
        
        const calculatedWidth = (containerWidth - (gap * (itemsVisible - 1))) / itemsVisible;
        setItemWidth(calculatedWidth);
      }
    };

    updateItemWidth();
    window.addEventListener('resize', updateItemWidth);
    return () => window.removeEventListener('resize', updateItemWidth);
  }, [itemsPerView]);

  useEffect(() => {
    const updateScrollButtons = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    updateScrollButtons();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollButtons);
      return () => carousel.removeEventListener('scroll', updateScrollButtons);
    }
  }, [vendors]);

  // Touch event handlers for smooth vertical scrolling
  const handleTouchStart = (e) => {
    setIsDragging(true);
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    // Allow horizontal scrolling within carousel
    const touch = e.touches[0];
    const carousel = carouselRef.current;
    if (carousel) {
      const rect = carousel.getBoundingClientRect();
      const isWithinCarousel = touch.clientX >= rect.left && touch.clientX <= rect.right;
      
      if (isWithinCarousel) {
        // Prevent vertical scrolling when touching carousel
        e.preventDefault();
      }
    }
  };

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = itemWidth + 16; // item width + gap
      const newScrollLeft = direction === 'left' 
        ? carouselRef.current.scrollLeft - scrollAmount
        : carouselRef.current.scrollLeft + scrollAmount;
      
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleViewAll = () => {
    onViewAll?.();
  };

  if (!vendors || vendors.length === 0) {
    return (
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <Icon name="Search" size={48} color="#8B7B6F" className="mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-text-secondary mb-2">
              No vendors found
            </h3>
            <p className="text-text-muted font-caption">
              Try adjusting your search criteria or check back later.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-2">
              {title}
            </h2>
            {subtitle && (
              <p className="text-text-secondary font-caption">
                {subtitle}
              </p>
            )}
          </div>
          
          {showViewAll && (
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={16}
              onClick={handleViewAll}
              className="hidden sm:flex"
            >
              View All
            </Button>
          )}
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons - Desktop */}
          <div className="hidden lg:block">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-medium border border-border flex items-center justify-center transition-smooth ${
                canScrollLeft 
                  ? 'text-text-primary hover:text-primary hover:shadow-strong' 
                  : 'text-text-muted cursor-not-allowed opacity-50'
              }`}
            >
              <Icon name="ChevronLeft" size={20} color="currentColor" />
            </button>
            
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-medium border border-border flex items-center justify-center transition-smooth ${
                canScrollRight 
                  ? 'text-text-primary hover:text-primary hover:shadow-strong' 
                  : 'text-text-muted cursor-not-allowed opacity-50'
              }`}
            >
              <Icon name="ChevronRight" size={20} color="currentColor" />
            </button>
          </div>

          {/* Carousel */}
          <div
            ref={carouselRef}
            className="flex space-x-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              touchAction: 'pan-x',
              WebkitOverflowScrolling: 'touch'
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
          >
            {vendors.map((vendor) => (
              <div
                key={vendor.id}
                className="flex-shrink-0"
                style={{ width: itemWidth || 'auto' }}
              >
                <VendorCard
                  vendor={vendor}
                  variant={variant}
                  onMessage={onMessage}
                  onViewProfile={onViewProfile}
                  onToggleFavorite={onToggleFavorite}
                />
              </div>
            ))}
          </div>

          {/* Mobile Navigation Dots */}
          <div className="lg:hidden flex justify-center space-x-2 mt-4">
            {Array.from({ length: Math.ceil(vendors.length / itemsPerView.mobile) }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const scrollAmount = (itemWidth + 16) * itemsPerView.mobile * index;
                  carouselRef.current?.scrollTo({ left: scrollAmount, behavior: 'smooth' });
                }}
                className={`w-2 h-2 rounded-full transition-smooth ${
                  Math.floor(currentIndex / itemsPerView.mobile) === index
                    ? 'bg-primary' :'bg-border hover:bg-border-dark'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Mobile View All Button */}
        {showViewAll && (
          <div className="sm:hidden mt-6 text-center">
            <Button
              variant="outline"
              size="md"
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={16}
              onClick={handleViewAll}
              fullWidth
            >
              View All {title}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default VendorCarousel;